import { Tooltip } from "@mui/material";
import Translate from "./Translate";
import { Discount, ProductionQuantityLimits } from "@mui/icons-material";
import { groupBy } from "lodash";

export const partType = {
  book: 1,
  course: 2,
  serviceAgreement: 3,
  teacher: 4,
  storeGoods: 5,
  freight: 6,
  resourceThing: 7,
  eLearning: 8,
  license: 9,
  plasticCardPrintJob: 10,
  ePortProgamLicense: 11,
  ePortUserLicense: 12,
  SMS: 13,
  ePortReportClient: 14,
  iframe: 15,
  courseAdminHTML: 17,
  drivingPermitsHTML: 18,
  drivingPermitsHTMLUser: 19,
  courseAdminReadOnly: 20,
  userReadOnly: 21,
  extraFinalExam: 22,
  resellableExtraFinalExam: 23,
  digitalLicense: 24,
  plasticCardPrintJobAndDigitalLicense: 25,
  permitResponsibleView: 26,
  permitBroker: 27,
  permitBrokerUser: 28,
  permitsReadOnly: 29,
  permitsUserReadOnly: 30,
  freight25Vat: 61,
  freight12Vat: 62,
  freight06Vat: 63,
  freightEUVat: 64,
  freightOutsideEUVat: 65,
  resellableELearningLicense: 88,
  expired: 99,
};

export function isFreightPartType(partTypeParam) {
  if (partTypeParam === undefined || partTypeParam === null) {
    return partTypeParam;
  }

  const partTypeNumber = typeof partTypeParam === "number" ? partTypeParam : Number(partTypeParam);
  return [
    partType.freight,
    partType.freight06Vat,
    partType.freight12Vat,
    partType.freight25Vat,
    partType.freightEUVat,
    partType.freightOutsideEUVat,
  ].some((pt) => pt === partTypeNumber);
}

export function isCoursePartType(partTypeParam) {
  if (partTypeParam === undefined || partTypeParam === null) {
    return partTypeParam;
  }

  const partTypeNumber = typeof partTypeParam === "number" ? partTypeParam : Number(partTypeParam);
  return [partType.course, partType.eLearning].some((pt) => pt === partTypeNumber);
}

export function isPhysicalPickPartType(partTypeParam) {
  if (partTypeParam === undefined || partTypeParam === null) {
    return partTypeParam;
  }

  const partTypeNumber = typeof partTypeParam === "number" ? partTypeParam : Number(partTypeParam);
  return [
    partType.book,
    partType.storeGoods,
    partType.plasticCardPrintJob,
    partType.plasticCardPrintJobAndDigitalLicense,
  ].some((pt) => pt === partTypeNumber);
}

export function isCardPrintPartType(partTypeParam) {
  if (partTypeParam === undefined || partTypeParam === null) {
    return partTypeParam;
  }

  const partTypeNumber = typeof partTypeParam === "number" ? partTypeParam : Number(partTypeParam);
  return [partType.plasticCardPrintJob, partType.plasticCardPrintJobAndDigitalLicense].some(
    (pt) => pt === partTypeNumber,
  );
}

export function isDigitalPickPartType(partTypeParam) {
  if (partTypeParam === undefined || partTypeParam === null) {
    return partTypeParam;
  }

  const partTypeNumber = typeof partTypeParam === "number" ? partTypeParam : Number(partTypeParam);
  return [partType.resellableELearningLicense, partType.resellableExtraFinalExam].some((pt) => pt === partTypeNumber);
}

function isNotAutoPickPartType(partTypeParam) {
  if (partTypeParam === undefined || partTypeParam === null) {
    return partTypeParam;
  }

  const partTypeNumber = typeof partTypeParam === "number" ? partTypeParam : Number(partTypeParam);

  return [
    partType.book,
    partType.storeGoods,
    partType.license,
    partType.plasticCardPrintJob,
    partType.plasticCardPrintJobAndDigitalLicense,
    partType.resellableExtraFinalExam,
    partType.resellableELearningLicense,
  ].some((pt) => pt === partTypeNumber);
}

export function isAutoPickPartType(partTypeParam) {
  return !isNotAutoPickPartType(partTypeParam);
}

export function canAssembleMultiPart(part) {
  return (
    part.children &&
    part.children.length > 0 &&
    part.partType !== partType.resellableELearningLicense &&
    part.partType !== partType.resellableExtraFinalExam &&
    part.partType !== partType.plasticCardPrintJobAndDigitalLicense
  );
}

export function getPartStatus(part) {
  let color;
  let title;
  let icon;

  const needInOrder = part.needInOrder;
  const hasInOrder = part.inOrders && part.inOrders.length > 0;

  if (needInOrder) {
    color = "warning";
    title = Translate.get("PartNeedInOrderFilterLabel");
    icon = <ProductionQuantityLimits />;
  } else if (hasInOrder) {
    color = "success";
    title = Translate.get("PartHasInOrderFilterLabel");
    icon = <ProductionQuantityLimits />;
  }

  const className = `iconbox ${color}`;

  if (color && title && icon) {
    return (
      <Tooltip arrow className={className} title={title}>
        {icon}
      </Tooltip>
    );
  } else {
    return null;
  }
}

export function getPartExtraDiscountParents(part) {
  if (!part.partExtraDiscountParents || part.partExtraDiscountParents.length === 0) return null;

  const items = part.partExtraDiscountParents
    .sort((a, b) => (a.fixedPrice ?? a.fixedPercent) - (b.fixedPrice ?? b.fixedPercent))
    .map((x) => ({
      parentName: x.partDescription,
      // sortKey: x.fixedPrice ?? x.fixedPercent,
      discount: `${x.fixedPrice ?? `${x.fixedPercent * 100} %`} (${x.currency})`,
    }));

  const groupedItems = groupBy(items, (item) => item.discount);
  const text = Object.entries(groupedItems)
    .map(
      ([key, items]) =>
        key +
        "\n" +
        items
          .map((x) => x.parentName)
          .sort((a, b) => a.localeCompare(b, "sv"))
          .join("\n"),
    )
    .join("\n\n");

  return (
    <Tooltip arrow className="iconbox" title={<div style={{ whiteSpace: "pre-line", textWrap: "nowrap" }}>{text}</div>}>
      <Discount />
    </Tooltip>
  );
}
